/** Direct-link page of the core service. It is intentionally not listed in any navigation menu. */
export const CORE_SERVICE_PATH = '/core-service';

export const CORE_SERVICE_TITLE = 'Core service';

export enum CoreServiceTab {
  TODAY = 'today',
  BLUEPRINTS = 'blueprints',
  TREE = 'tree',
  ALLOCATION = 'allocation',
  EVENTS = 'events',
}

export const CORE_SERVICE_TABS: { id: CoreServiceTab; label: string }[] = [
  { id: CoreServiceTab.TODAY, label: 'Syberry Today' },
  { id: CoreServiceTab.BLUEPRINTS, label: 'Blueprints' },
  { id: CoreServiceTab.TREE, label: 'Tree' },
  { id: CoreServiceTab.ALLOCATION, label: 'Cost allocation' },
  { id: CoreServiceTab.EVENTS, label: 'Live events' },
];

export const CORE_SERVICE_TAB_PARAM = 'tab';
export const CORE_SERVICE_PERSON_PARAM = 'as';
export const CORE_SERVICE_STORAGE_PREFIX = 'core-service';

export const SLA_SOON_SECONDS = 4 * 60 * 60;
export const SYNC_STATUS_POLL_MS = 5000;
export const EVENTS_POLL_MS = 3000;
export const EVENTS_KEEP_LIMIT = 300;
export const TREE_PAGE_SIZE = 50;
export const TREE_CHILDREN_PAGE_SIZE = 200;

export const OBLIGATION_TIER_LABELS: Record<string, string> = {
  TRANSIENT_DIRECT: 'Must move — you own it',
  TRANSIENT_DYNAMIC: 'Queues you can take',
  STEADY_DIRECT: 'Ongoing — report time at your discretion',
  STEADY_DYNAMIC: 'Ongoing — shared',
};

export const DEFINITION_CATEGORY_LABELS: Record<string, string> = {
  ORGANIZATION: 'Organization',
  BUSINESS_FUNCTION: 'Business function',
  PEER_TO_PEER_TASK: 'Peer-to-peer task',
  SYSTEM_TO_HUMAN_TASK: 'System-to-human task',
};

export const MIXED_CATEGORY = 'MIXED';
export const MIXED_CATEGORY_LABEL = 'Mixed categories';

type GapGroup = 'structure' | 'model' | 'info';

export const GAP_DETAILS: Record<string, { group: GapGroup; label: string; hint: string }> = {
  NO_WORKFLOW: {
    group: 'structure',
    label: 'No workflow',
    hint: 'The tracker has no workflow transitions in Redmine; states are only observed on tickets',
  },
  NO_INITIAL_STATE: {
    group: 'structure',
    label: 'No initial state',
    hint: 'The tracker has no default status; the initial state is guessed',
  },
  DEAD_END_STATE: {
    group: 'structure',
    label: 'Dead end',
    hint: 'An open state has no outgoing transitions: tickets can get stuck there',
  },
  UNREACHABLE_STATE: {
    group: 'structure',
    label: 'Unreachable state',
    hint: 'No transition leads into this state',
  },
  OBSERVED_STATE_NOT_IN_WORKFLOW: {
    group: 'structure',
    label: 'State outside workflow',
    hint: 'Tickets sit in a status the workflow does not know',
  },
  UNKNOWN_OWNERSHIP_RESOLVER: {
    group: 'structure',
    label: 'Unknown owner resolver',
    hint: 'An overlay names an owner resolver or role that does not exist',
  },
  NO_SLA: { group: 'model', label: 'SLA', hint: 'Transient states without SLA: add sla in an overlay' },
  STATE_KIND_INFERRED: {
    group: 'model',
    label: 'State kinds',
    hint: 'Transient vs steady is a guess: mark steady states in an overlay',
  },
  OWNER_INFERRED: {
    group: 'model',
    label: 'Owners',
    hint: 'Owner is assumed to be the assignee: confirm or change in an overlay',
  },
  CATEGORY_INFERRED: {
    group: 'model',
    label: 'Category',
    hint: 'Peer-to-peer vs system-to-human is inferred from who authors tickets',
  },
  UNUSED_DEFINITION: { group: 'info', label: 'Unused', hint: 'The tracker has no tickets' },
};

export const GAP_GROUPS: { id: GapGroup; title: string; hint: string }[] = [
  { id: 'structure', title: 'Structure problems', hint: 'Workflow shapes the core cannot run as is' },
  {
    id: 'model',
    title: 'To complete in overlays',
    hint: 'What Redmine does not know: SLA, steady states, owners, category',
  },
  { id: 'info', title: 'Info', hint: '' },
];

export const ORIGIN_CHANNEL_LABELS: Record<string, string> = {
  ONE_STOP: 'One-Stop request',
  BOT: 'a bot',
  ANONYMOUS: 'anonymous',
};
